import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  Platform,
  KeyboardAvoidingView,
  ActivityIndicator,
} from 'react-native';
import {
  TextInput,
  Button,
  Text,
  Card,
  Title,
  HelperText,
  IconButton,
} from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { colors, spacing, typography } from '../../constants/theme';
import { authService } from '../../services/authService';
import { AuthStackParamList } from '../../navigation/AuthNavigator';
import { getErrorMessage } from '../../utils/errorMessages';

type ResetPasswordScreenNavigationProp = StackNavigationProp<AuthStackParamList, 'ResetPassword'>;

interface ResetPasswordScreenWebProps {
  token?: string;
}

const ResetPasswordScreenWeb: React.FC<ResetPasswordScreenWebProps> = ({ token }) => {
  const navigation = useNavigation<ResetPasswordScreenNavigationProp>();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidatingToken, setIsValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  useEffect(() => {
    if (token) {
      validateToken();
    }
  }, [token]);

  const validateToken = async () => {
    try {
      await authService.validateResetToken(token!);
      setTokenValid(true);
    } catch (error: unknown) {
      setTokenValid(false);
      console.error('Token validation failed:', error);
    } finally {
      setIsValidatingToken(false);
    }
  };

  const handleResetPassword = async () => {
    if (password !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    try {
      await authService.resetPassword(token!, password);
      alert('Password reset successfully! Please login with your new password.');
      // Navigate to login screen
      if (Platform.OS === 'web') {
        window.location.href = '/';
      }
    } catch (error: unknown) {
      alert(getErrorMessage(error, 'Failed to reset password'));
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidatingToken) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={styles.loadingText}>Validating reset link...</Text>
      </View>
    );
  }

  if (!tokenValid) {
    return (
      <View style={styles.container}>
        <Card style={styles.card}>
          <Card.Content>
            <Title style={styles.title}>Invalid or Expired Link</Title>
            <Text style={styles.errorText}>
              This password reset link is invalid or has expired.
              Please request a new password reset link.
            </Text>
            <Button
              mode="contained"
              onPress={() => {
                if (Platform.OS === 'web') {
                  window.location.href = '/forgot-password';
                }
              }}
              style={styles.button}
            >
              Request New Link
            </Button>
          </Card.Content>
        </Card>
      </View>
    );
  }

  return (
    <ScrollView contentContainerStyle={styles.scrollContainer}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.container}
      >
        <Card style={styles.card}>
          <Card.Content>
            <Title style={styles.title}>Reset Your Password</Title>
            <Text style={styles.subtitle}>
              Enter your new password below
            </Text>

            <View style={styles.inputContainer}>
              <TextInput
                label="New Password"
                value={password}
                onChangeText={setPassword}
                secureTextEntry={!showPassword}
                mode="outlined"
                style={styles.input}
                right={
                  <TextInput.Icon
                    icon={showPassword ? 'eye-off' : 'eye'}
                    onPress={() => setShowPassword(!showPassword)}
                  />
                }
              />
              <HelperText type="info" visible={true}>
                Password must be at least 6 characters
              </HelperText>
            </View>

            <View style={styles.inputContainer}>
              <TextInput
                label="Confirm Password"
                value={confirmPassword}
                onChangeText={setConfirmPassword}
                secureTextEntry={!showConfirmPassword}
                mode="outlined"
                style={styles.input}
                right={
                  <TextInput.Icon
                    icon={showConfirmPassword ? 'eye-off' : 'eye'}
                    onPress={() => setShowConfirmPassword(!showConfirmPassword)}
                  />
                }
              />
              <HelperText
                type="error"
                visible={confirmPassword.length > 0 && password !== confirmPassword}
              >
                Passwords do not match
              </HelperText>
            </View>

            <Button
              mode="contained"
              onPress={handleResetPassword}
              loading={isLoading}
              disabled={
                isLoading ||
                password.length < 6 ||
                password !== confirmPassword
              }
              style={styles.button}
            >
              Reset Password
            </Button>

            <Button
              mode="text"
              onPress={() => {
                if (Platform.OS === 'web') {
                  window.location.href = '/';
                }
              }}
              style={styles.linkButton}
            >
              Back to Login
            </Button>
          </Card.Content>
        </Card>
      </KeyboardAvoidingView>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scrollContainer: {
    flexGrow: 1,
  },
  container: {
    flex: 1,
    justifyContent: 'center',
    padding: spacing.lg,
    backgroundColor: colors.background,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.background,
  },
  loadingText: {
    marginTop: spacing.md,
    ...typography.body1,
    color: colors.textSecondary,
  },
  card: {
    maxWidth: 400,
    width: '100%',
    alignSelf: 'center',
    elevation: 4,
  },
  title: {
    ...typography.h1,
    color: colors.primary,
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  subtitle: {
    ...typography.body1,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: spacing.xl,
  },
  errorText: {
    ...typography.body1,
    color: colors.error,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  inputContainer: {
    marginBottom: spacing.md,
  },
  input: {
    backgroundColor: colors.surface,
  },
  button: {
    marginTop: spacing.lg,
    paddingVertical: spacing.sm,
  },
  linkButton: {
    marginTop: spacing.md,
  },
});

export default ResetPasswordScreenWeb;
